import { CSSProperties, MouseEvent as ReactMouseEvent, useCallback, useRef, useState } from "react";

// The floating review cards (report-editor suggestions, form-field suggestions)
// share one drag behavior: grab the header and move the card anywhere. Until it
// is moved (or positioned by the caller) the card sits bottom-center.
//
//   const drag = useDraggableCard();
//   <div className="fixed z-[9999] pointer-events-none" style={drag.wrapperStyle}>
//     <div ref={drag.cardRef}>
//       <div onMouseDown={drag.handleDragStart} className="cursor-grab">header</div>
//
// Drags are delta-based (mouse movement, not absolute coordinates), so they do
// not depend on the card's containing block: a transformed ancestor can
// re-parent a `position: fixed` element and make viewport and containing-block
// coordinates disagree. `position.left` is the card's CENTER x, because the
// wrapper centers it with translateX(-50%).
export interface CardPosition {
  top: number;
  left: number;
}

const MIN_VISIBLE = 48; // keep at least this much of the card on screen

export function useDraggableCard() {
  const [position, setPosition] = useState<CardPosition | null>(null);
  const positionRef = useRef<CardPosition | null>(null);
  positionRef.current = position;
  const cardRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const start = useRef({ mouseX: 0, mouseY: 0, top: 0, left: 0 });

  const handleDragStart = useCallback((e: ReactMouseEvent) => {
    if (e.button !== 0) return;
    // Buttons in the header (close, etc.) are clicks, not drags.
    if ((e.target as HTMLElement).closest("button, a, input, textarea, select")) return;

    let current = positionRef.current;
    if (!current) {
      // Still in the default bottom-center spot: start from where it is drawn.
      const el = cardRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      current = { top: r.top, left: r.left + r.width / 2 };
    }
    dragging.current = true;
    start.current = { mouseX: e.clientX, mouseY: e.clientY, top: current.top, left: current.left };

    const move = (ev: MouseEvent) => {
      if (!dragging.current) return;
      ev.preventDefault();
      const s = start.current;
      setPosition({
        top: Math.max(0, Math.min(window.innerHeight - MIN_VISIBLE, s.top + (ev.clientY - s.mouseY))),
        left: Math.max(MIN_VISIBLE, Math.min(window.innerWidth - MIN_VISIBLE, s.left + (ev.clientX - s.mouseX))),
      });
    };
    const end = () => {
      dragging.current = false;
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", end);
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", end);
  }, []);

  const wrapperStyle: CSSProperties = position
    ? { top: position.top, left: position.left, transform: "translateX(-50%)", maxWidth: "min(90vw, 520px)" }
    : { inset: 0, display: "flex", alignItems: "flex-end", justifyContent: "center", paddingBottom: "2rem" };

  return { position, setPosition, cardRef, handleDragStart, wrapperStyle };
}
